import * as _ from 'lodash';
import type { Action as ReduxAction } from 'redux';

import { errorMessagesListFromResponse } from '../helpers/errors';
import type { FailedFetchResponse } from '../helpers/errors';
import type { AsyncStatus, RouterProps } from '../types';

export type ActionMeta = Record<string, any>;

export type Action<Payload = unknown, Type = string, Meta = ActionMeta> = ReduxAction<Type> & {
  payload?: Payload;
  meta?: Meta;
};

type Reducer<S, A extends Action> = (state: S, action: A) => S;

export type ReducersMap<S, H> = { [K in string & keyof H]: Reducer<S, Action<any, K>> };

export function createReducer<S, H extends ReducersMap<S, H> = {}>(
  initialState: S,
  handlers: H,
): Reducer<S, Action<any, string & keyof H>> {
  return (state = initialState, action) => {
    if (handlers.hasOwnProperty(action.type)) {
      return handlers[action.type](state, action);
    }
    return state;
  };
}

export const getQueryTeamId = (_state: unknown, props: RouterProps) => props.location.query.teamId;

/**
 * getInitialStatus is a utility method that redux stores call to retrieve the initial status state. This can be called
 * during initialization, as well as inside the *_FULFILLED reducers
 */
export const getInitialStatus = (): AsyncStatus => ({
  isLoading: false,
  loading: false,
  errors: [],
});

/**
 * getPendingStatus is a utility method that redux stores call to retrieve the pending status state from a *_PENDING reducer
 */
export const getPendingStatus = (): AsyncStatus => ({
  isLoading: true,
  loading: true,
  errors: [],
});

/**
 * getErrorStatus is a utility method that redux stores call to retrieve the status state from a *_REJECTED reducer
 */
export const getErrorStatus = (errorPayload: FailedFetchResponse): AsyncStatus => ({
  isLoading: false,
  loading: false,
  errors: errorMessagesListFromResponse(errorPayload),
});

export type AsyncActionTypes<T extends string> = {
  PENDING: `${T}_PENDING`;
  FULFILLED: `${T}_FULFILLED`;
  REJECTED: `${T}_REJECTED`;
};

/**
 * Takes an action type for an async action and
 * returns the string-concatenated async action types
 * for use in a reducer.
 *
 * @param {string} actionType - Any action type.
 * @example
 * // Before:
 * [`${actionTypes.someAction}_PENDING`]: (state, action) => { ... }
 * // After:
 * [`${asyncActionType(actionTypes.someAction).PENDING}`]: (state, action) => { ... }
 */
export const asyncActionType = _.memoize(
  (actionType: string): AsyncActionTypes<typeof actionType> => ({
    PENDING: `${actionType}_PENDING`,
    FULFILLED: `${actionType}_FULFILLED`,
    REJECTED: `${actionType}_REJECTED`,
  }),
);

/**
 * Takes an object of actionTypes which should be async and adds the asyncActionType for them.
 *
 * @param {Record<string, string>} actionTypes - The actionTypes object.
 * @example
 * const actionTypes = {
 *   FETCH_USER: 'FETCH_USER',
 * };
 *
 * const asyncActionTypes = toAsyncActionTypes(actionTypes);
 * // > asyncActionTypes.FETCH_USER.PENDING
 * // > asyncActionTypes.FETCH_USER.FULFILLED
 * // > asyncActionTypes.FETCH_USER.REJECTED
 */
export const toAsyncActionTypes = <T extends Record<string, string>>(
  actionTypes: T,
): Record<keyof T, AsyncActionTypes<T[keyof T]>> => {
  return Object.fromEntries(
    Object.entries(actionTypes).map(([key, actionType]) => {
      // Cast key and actionType appropriately
      const typedKey = key as keyof T;
      const typedActionType = actionType as T[keyof T];

      return [typedKey, asyncActionType(typedActionType)];
    }),
  ) as Record<keyof T, AsyncActionTypes<T[keyof T]>>;
};
